import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Building2, Users, Star, Calculator, Phone, Shield, Award, TrendingDown, AlertCircle, CheckCircle } from 'lucide-react';
import './BusinessHero.css';
import SavingsReviewModal from './SavingsReviewModal';

// Organic blob backgrounds
const OrganicBlobs = () => (
  <div className="organic-blobs">
    <div className="organic-blob blob-1"></div>
    <div className="organic-blob blob-2"></div>
    <div className="organic-blob blob-3"></div>
    <div className="organic-blob blob-4"></div>
  </div>
);

// Enhanced floating particles
const EnhancedParticles = () => (
  <div className="enhanced-particles">
    {[...Array(30)].map((_, i) => (
      <div 
        key={i} 
        className={`enhanced-particle particle-${i % 6}`}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          width: `${Math.random() * 8 + 2}px`,
          height: `${Math.random() * 8 + 2}px`,
          animationDelay: `${Math.random() * 10}s`,
          animationDuration: `${Math.random() * 15 + 10}s`
        }}
      />
    ))}
  </div>
);

// Consultative headline slider
const ConsultativeHeadlineSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const headlines = [
    "Reduce Telecom Costs Across Your Team — Without Switching Providers",
    "Cut Business Phone Bills by 60%. Keep Your Carrier."
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % headlines.length);
    }, 6000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="headline-slider">
      <AnimatePresence mode="wait">
        {headlines.map((headline, index) => (
          index === currentIndex && (
            <motion.h1 
              key={headline}
              className="hero-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              {headline}
            </motion.h1>
          )
        ))}
      </AnimatePresence>
    </div>
  );
};

// Trust badges component
const TrustBadges = () => (
  <motion.div 
    className="trust-badges"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.4 }}
  >
    <motion.div 
      className="trust-badge"
      whileHover={{ scale: 1.05 }}
    >
      <Shield size={16} />
      <span>Enterprise-Grade Service</span>
    </motion.div>
    <motion.div 
      className="trust-badge"
      whileHover={{ scale: 1.05 }}
    >
      <Award size={16} />
      <span>No Prepaid Plans</span>
    </motion.div>
    <motion.div 
      className="trust-badge"
      whileHover={{ scale: 1.05 }}
    >
      <Star size={16} />
      <span>Trusted by 100+ Teams</span>
    </motion.div>
  </motion.div>
);

// Consultative feature item
const ConsultativeFeatureItem = ({ mainText, supportingText, delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div 
      className="enhanced-feature"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.8 + delay * 0.15 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ scale: 1.03 }}
    >
      <motion.div 
        className="enhanced-feature-icon-container"
        animate={{ 
          boxShadow: isHovered 
            ? "0 15px 30px rgba(6, 182, 212, 0.3), 0 0 0 2px rgba(6, 182, 212, 0.4)" 
            : "0 10px 20px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(6, 182, 212, 0.2)"
        }}
      >
        <Check size={18} className="enhanced-feature-icon" />
        <motion.div 
          className="icon-glow"
          animate={{
            opacity: isHovered ? 1 : 0.6,
            scale: isHovered ? 1.2 : 1
          }}
          transition={{ duration: 0.5 }}
        />
      </motion.div>
      <div className="enhanced-feature-text-container">
        <span className="enhanced-feature-text">{mainText}</span>
        <span className="enhanced-feature-subtext">{supportingText}</span>
      </div>
    </motion.div>
  );
};

// Enhanced Cost Optimization Calculator Card with Mobile Improvements
const CostOptimizationCalculator = () => {
  const [selectedCarrier, setSelectedCarrier] = useState('verizon');
  const [teamSize, setTeamSize] = useState(50);
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Calculate savings based on real market data
  const calculateSavings = () => {
    const carrierData = {
      verizon: { typical: 85, optimized: 35, name: 'Verizon' },
      att: { typical: 80, optimized: 35, name: 'AT&T' },
      tmobile: { typical: 85, optimized: 35, name: 'T-Mobile' }
    };
    
    const data = carrierData[selectedCarrier];
    const monthlySavingsPerLine = data.typical - data.optimized;
    const monthlySavings = teamSize * monthlySavingsPerLine;
    const annualSavings = monthlySavings * 12;
    const annualSavingsPerEmployee = annualSavings / teamSize;
    const percentageSaved = Math.round((monthlySavingsPerLine / data.typical) * 100);
    
    return {
      monthlySavings,
      annualSavings,
      annualSavingsPerEmployee,
      monthlySavingsPerLine,
      percentageSaved,
      carrierName: data.name,
      typicalCost: data.typical,
      optimizedCost: data.optimized
    };
  };
  
  const savings = calculateSavings();
  
  // Format numbers for better mobile display
  const formatCurrency = (amount) => {
    if (isMobile && amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K`;
    }
    return `${amount.toLocaleString()}`;
  };
  
  return (
    <motion.div 
      className="cost-optimization-card"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      whileHover={!isMobile ? { scale: 1.02 } : {}}
    >
      {/* Header */}
      <div className="optimization-card-header">
        <div className="optimization-card-badge">
          <Shield size={isMobile ? 12 : 14} />
          <span>Business telecom audit. Same carrier, lower cost.</span>
        </div>
        
        <h2 className="optimization-card-title">
          Business Cost Calculator
        </h2>
        <p className="optimization-card-subtitle">
          See how much your company could save annually
        </p>
      </div>
      
      {/* Interactive Inputs */}
      <div className="optimization-card-inputs">
        <div className="input-group">
          <label className="input-label">
            Your current carrier:
          </label>
          <div className="carrier-buttons">
            {[
              { key: 'verizon', label: 'Verizon' },
              { key: 'att', label: 'AT&T' },
              { key: 'tmobile', label: 'T-Mobile' }
            ].map((carrier) => (
              <button
                key={carrier.key}
                onClick={() => setSelectedCarrier(carrier.key)}
                className={`carrier-button ${selectedCarrier === carrier.key ? 'selected' : ''}`}
              >
                {carrier.label}
              </button>
            ))}
          </div>
        </div>
        
        <div className="input-group">
          <label className="input-label">
            Team size: <span className="team-size-value">{teamSize}</span> lines
          </label>
          <input
            type="range"
            min="5"
            max="1000"
            value={teamSize}
            onChange={(e) => setTeamSize(Number(e.target.value))}
            className="team-size-slider"
            // Mobile-specific attributes
            style={isMobile ? { touchAction: 'manipulation' } : {}}
          />
          <div className="slider-labels">
            <span>5</span>
            <span>1000</span>
          </div>
        </div>
      </div>
      
      {/* Market Reality Check */}
      <div className="market-reality-section">
        <h3 className="market-reality-title">
          <TrendingDown size={isMobile ? 16 : 18} />
          Market Reality Check
        </h3>
        
        <div className="pricing-comparison">
          <div className="pricing-item typical">
            <div className="pricing-item-content">
              <AlertCircle size={isMobile ? 14 : 16} />
              <span className="pricing-item-label">
                Most {savings.carrierName} business plans
              </span>
            </div>
            <span className="pricing-item-value">
              ${savings.typicalCost}/mo
            </span>
          </div>
          
          <div className="pricing-item optimized">
            <div className="pricing-item-content">
              <CheckCircle size={isMobile ? 14 : 16} />
              <span className="pricing-item-label">
                After optimization (same carrier)
              </span>
            </div>
            <span className="pricing-item-value">
              ${savings.optimizedCost}/mo
            </span>
          </div>
        </div>
        
        <div className="cost-reduction-highlight">
          <div className="cost-reduction-label">Cost reduction per line</div>
          <div className="cost-reduction-value">
            {savings.percentageSaved}% less
          </div>
        </div>
      </div>
      
      {/* ROI Calculation */}
      <motion.div 
        className="roi-section"
        whileHover={!isMobile ? { scale: 1.02 } : {}}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <h3 className="roi-title">
          <Calculator size={isMobile ? 16 : 20} />
          Your Potential ROI
        </h3>
        
        <div className="savings-grid">
          <div className="savings-item">
            <div className="savings-item-label">Monthly Savings</div>
            <div className="savings-item-value">
              {formatCurrency(savings.monthlySavings)}
            </div>
          </div>
          <div className="savings-item">
            <div className="savings-item-label">Annual Savings</div>
            <div className="savings-item-value">
              {formatCurrency(savings.annualSavings)}
            </div>
          </div>
        </div>

        {/* Enhanced Per Employee Highlight */}
        <motion.div 
          className="per-employee-highlight"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          whileHover={!isMobile ? { scale: 1.02 } : {}}
        >
          <div className="per-employee-content">
            <div className="per-employee-label">Annual Savings Per Employee</div>
            <div className="per-employee-value">
              {formatCurrency(Math.round(savings.annualSavingsPerEmployee))}
            </div>
            <div className="per-employee-description">saved per year, per employee</div>
          </div>
        </motion.div>
        
        <div className="savings-disclaimer">
          <strong>Real savings based on published {savings.carrierName} pricing + actual customer outcomes.</strong> Results vary based on your current setup and optimization opportunities.
        </div>

        {/* NEW: In-Calculator CTA */}
        <motion.button 
          className="calculator-cta-button"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.4 }}
          whileHover={{ scale: isMobile ? 1 : 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            // This would trigger the modal or scroll to main CTA
            document.querySelector('.enhanced-cta')?.click();
          }}
        >
          <Calculator size={16} />
          <span>See My Full Savings Plan</span>
          <ArrowRight size={14} />
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

// Consultative CTA buttons
const ConsultativeCTAButton = ({ children, variant = "primary", icon, ...props }) => {
  const isPrimary = variant === "primary";
  
  return (
    <motion.button 
      className={`enhanced-cta glass-button ${variant === "secondary" ? "secondary" : ""}`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 1.2 }}
      whileHover={{ 
        scale: 1.05, 
        boxShadow: isPrimary 
          ? "0 20px 40px rgba(6, 182, 212, 0.45), 0 0 0 2px rgba(255, 255, 255, 0.15) inset"
          : "0 20px 40px rgba(6, 182, 212, 0.35), 0 0 0 2px rgba(255, 255, 255, 0.15) inset"
      }}
      whileTap={{ scale: 0.98 }}
      {...props}
    >
      <span className="button-text">{children}</span>
      <div className="button-icon-container">
        {icon || <ArrowRight size={18} className="button-icon" />}
      </div>
      <motion.div 
        className="button-glow" 
        whileHover={{ opacity: 1 }}
        initial={{ opacity: 0 }}
      />
    </motion.button>
  );
};

// Positioning statement component
const PositioningStatement = () => (
  <motion.div 
    className="positioning-statement"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 1.4 }}
    whileHover={{ 
      scale: 1.02,
      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.15) inset"
    }}
  >
    <div className="positioning-content">
      <span className="positioning-tagline">"We don't sell you a plan. We optimize what you have and manage what you need."</span>
      <span className="positioning-description">
        Telecom audit • Enterprise group rates • Device management • Same carrier
      </span>
    </div>
    <div className="positioning-glow"></div>
  </motion.div>
);

const BusinessHero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const heroRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const handleOpenModal = () => {
    setIsModalOpen(true);
    // Track modal open event
    if (window.gtag) {
      window.gtag('event', 'savings_review_started', {
        event_category: 'Lead Generation',
        event_label: 'Business Hero CTA'
      });
    }
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  
  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrollY(scrollPosition);
      
      const heroElement = heroRef.current;
      if (heroElement && window.innerWidth > 768) {
        // Enhanced parallax effect on scroll with varying speeds
        const blobs = heroElement.querySelectorAll('.organic-blob');
        blobs.forEach((blob, index) => {
          const speed = 0.03 + (index * 0.02);
          const rotate = scrollPosition * 0.02;
          blob.style.transform = `translateY(${scrollPosition * speed}px) rotate(${rotate}deg)`;
        });
        
        // Parallax for other elements
        const title = heroElement.querySelector('.headline-slider');
        const features = heroElement.querySelector('.enhanced-features');
        const card = heroElement.querySelector('.cost-optimization-card');
        
        if (title) title.style.transform = `translateY(${scrollPosition * -0.05}px)`;
        if (features) features.style.transform = `translateY(${scrollPosition * -0.03}px)`;
        if (card) card.style.transform = `translateY(${scrollPosition * -0.01}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <section className="hero-section-wrapper teal-theme business-hero" ref={heroRef}>
        {/* Advanced background elements */}
        <div className="noise-overlay"></div>
        <OrganicBlobs />
        <EnhancedParticles />
        
        {/* Color overlay gradients */}
        <div className="gradient-overlay gradient-1"></div>
        <div className="gradient-overlay gradient-2"></div>
        
        <div className="hero-container">
          <div className="hero-content">
            <ConsultativeHeadlineSlider />
            
            <motion.p 
              className="hero-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              Your business is likely overpaying for Verizon, AT&T, or T-Mobile by 40-60%. We audit your current setup, <br className="hero-subtitle-break" />
              unlock enterprise discounts, and manage your team's devices — all without switching carriers.
            </motion.p>
            
            <TrustBadges />
            
            <motion.div className="enhanced-features">
              <ConsultativeFeatureItem 
                mainText="Keep Your Carrier, Cut Your Cost" 
                supportingText="Unlock enterprise-level group pricing — up to 60% off current rates" 
                delay={0} 
              />
              <ConsultativeFeatureItem 
                mainText="We Handle Device Management" 
                supportingText="SIM cards, eSIMs, device setup, and billing — managed for your team" 
                delay={1} 
              />
              <ConsultativeFeatureItem 
                mainText="Reduce IT & Finance Overhead" 
                supportingText="Centralized billing, streamlined admin, better features at lower cost" 
                delay={2} 
              />
            </motion.div>
            
            <div className="business-cta-container">
              <ConsultativeCTAButton 
                icon={<Calculator size={18} />}
                onClick={handleOpenModal}
              >
                Get Business Telecom Audit
              </ConsultativeCTAButton>
              <ConsultativeCTAButton variant="secondary" icon={<Phone size={18} />}>
                Talk to Enterprise Team
              </ConsultativeCTAButton>
            </div>
            
            <PositioningStatement />
          </div>
          
          <div className="hero-visual">
            <CostOptimizationCalculator />
            
            {/* Social Proof Below Calculator */}
            <motion.div 
              className="social-proof-section"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.8 }}
            >
              <div className="social-proof-content">
                <Star size={16} className="social-proof-icon" />
                <span className="social-proof-text">
                  Trusted by finance & operations teams at <strong>100+ startups</strong>
                </span>
              </div>
              <div className="social-proof-amount">
                <span className="amount-highlight">$3.2M+</span> saved for teams like yours
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Dedicated What Happens Next Section - Completely Separate */}
      <section className="what-happens-section">
        <motion.div 
          className="what-happens-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 2.0 }}
        >
          <h2 className="what-happens-section-title">📋 What Happens After You Sign Up?</h2>
          <div className="what-happens-grid">
            <motion.div 
              className="what-happens-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 2.2 }}
              whileHover={{ scale: 1.03, y: -5 }}
            >
              <div className="step-number-large">1</div>
              <div className="step-content-large">
                <h3>We audit your current plan</h3>
                <p>Review bills, uncover hidden charges & missed group discounts</p>
              </div>
            </motion.div>
            
            <motion.div 
              className="what-happens-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 2.4 }}
              whileHover={{ scale: 1.03, y: -5 }}
            >
              <div className="step-number-large">2</div>
              <div className="step-content-large">
                <h3>Custom optimization report</h3>
                <p>Detailed savings breakdown + implementation roadmap — free</p>
              </div>
            </motion.div>
            
            <motion.div 
              className="what-happens-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 2.6 }}
              whileHover={{ scale: 1.03, y: -5 }}
            >
              <div className="step-number-large">3</div>
              <div className="step-content-large">
                <h3>We handle the heavy lifting</h3>
                <p>Device management, SIM setup, billing coordination — all managed</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Savings Review Modal */}
      <SavingsReviewModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
      />
    </>
  );
};

export default BusinessHero;